import React, { useEffect, useState } from 'react';
import { OfficerLayout } from '../../components/layout/OfficerLayout';
import { ProgressStepper } from '../../components/ui/ProgressStepper';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Camera, MapPin, Upload, X, CheckCircle, Clock, AlertTriangle, ChevronRight, Eye, Edit2, Navigation, Video, Link as LinkIcon, Users, Loader2, Save, Wifi, WifiOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
// Types
interface ReportFormData {
  // Step 1: Kategori
  category: 'program' | 'series' | 'event' | '';
  programId: string;
  // Step 2: Detail
  title: string;
  activityType: string;
  description: string;
  // Step 3: Lokasi & Waktu
  date: string;
  time: string;
  latitude: string;
  longitude: string;
  locationName: string;
  // Step 4: Partisipasi
  participants: string;
  targetAudience: string[];
  // Step 5: Evidence
  photos: File[];
  videoUrl: string;
  proofUrl: string;
}
const initialFormData: ReportFormData = {
  category: '',
  programId: '',
  title: '',
  activityType: '',
  description: '',
  date: '',
  time: '',
  latitude: '-6.9175',
  longitude: '107.6191',
  locationName: '',
  participants: '',
  targetAudience: [],
  photos: [],
  videoUrl: '',
  proofUrl: ''
};
const TARGET_AUDIENCE_OPTIONS = [{
  id: 'masyarakat',
  label: 'Masyarakat Umum'
}, {
  id: 'pelajar',
  label: 'Pelajar/Mahasiswa'
}, {
  id: 'pedagang',
  label: 'Pedagang Pasar'
}, {
  id: 'ibu_rumah_tangga',
  label: 'Ibu Rumah Tangga'
}, {
  id: 'komunitas',
  label: 'Komunitas'
}, {
  id: 'asn',
  label: 'ASN/Pegawai'
}];
export function LaporanPage() {
  const [activeTab, setActiveTab] = useState<'create' | 'history'>('create');
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const [isOnline, setIsOnline] = useState(true);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  // Form State
  const [formData, setFormData] = useState<ReportFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>(['https://placehold.co/100x100/e2e8f0/1e293b?text=Foto+1', 'https://placehold.co/100x100/e2e8f0/1e293b?text=Foto+2']);
  // Auto-save draft every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (formData.title || formData.description) {
        setLastSaved(new Date());
        // In real app: save to localStorage or API
        localStorage.setItem('report_draft', JSON.stringify(formData));
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [formData]);
  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  const validateStep = (currentStep: number) => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    if (currentStep === 1) {
      if (!formData.category) newErrors.category = 'Kategori harus dipilih';
      if (!formData.programId) newErrors.programId = 'Program/Series/Event harus dipilih';
    } else if (currentStep === 2) {
      if (!formData.title) newErrors.title = 'Judul kegiatan wajib diisi';
      if (!formData.activityType) newErrors.activityType = 'Jenis kegiatan harus dipilih';
      if (!formData.description || formData.description.length < 50) {
        newErrors.description = 'Deskripsi minimal 50 karakter';
      }
    } else if (currentStep === 3) {
      if (!formData.date) newErrors.date = 'Tanggal wajib diisi';
      if (!formData.latitude || !formData.longitude) {
        newErrors.location = 'Lokasi GPS wajib diisi';
      }
    } else if (currentStep === 4) {
      if (!formData.participants || parseInt(formData.participants) < 1) {
        newErrors.participants = 'Jumlah peserta minimal 1';
      }
      if (formData.targetAudience.length === 0) {
        newErrors.targetAudience = 'Pilih minimal 1 sasaran';
      }
    } else if (currentStep === 5) {
      if (uploadedPhotos.length < 1) {
        newErrors.photos = 'Minimal 1 foto bukti kegiatan';
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      isValid = false;
    } else {
      setErrors({});
    }
    return isValid;
  };
  const handleNext = () => {
    if (validateStep(step)) {
      if (step < 6) setStep(step + 1);
    }
  };
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };
  const handleSubmit = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      localStorage.removeItem('report_draft');
    }, 1500);
  };
  const handleGetLocation = () => {
    setIsGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        setFormData({
          ...formData,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        });
        setIsGettingLocation(false);
      }, error => {
        console.error('Error getting location:', error);
        setIsGettingLocation(false);
      });
    } else {
      setIsGettingLocation(false);
    }
  };
  const toggleTargetAudience = (id: string) => {
    setFormData({
      ...formData,
      targetAudience: formData.targetAudience.includes(id) ? formData.targetAudience.filter(t => t !== id) : [...formData.targetAudience, id]
    });
  };
  const mockHistory = [{
    id: 'RPT-892',
    title: 'Sosialisasi RT 05',
    date: '20 Feb 2025',
    status: 'approved',
    program: 'Pilah Sampah'
  }, {
    id: 'RPT-891',
    title: 'Monitoring Bank Sampah',
    date: '18 Feb 2025',
    status: 'pending',
    program: 'HPSN 2025'
  }, {
    id: 'RPT-889',
    title: 'Aksi Bersih Sungai',
    date: '15 Feb 2025',
    status: 'rejected',
    program: 'Program Rutin',
    note: 'Foto kurang jelas'
  }, {
    id: 'RPT-888',
    title: 'Edukasi Pasar Tradisional',
    date: '12 Feb 2025',
    status: 'approved',
    program: 'Cegah Sampah Plastik'
  }];
  if (isSuccess) {
    return <OfficerLayout title="Laporan Terkirim" data-id="element-3157">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-in zoom-in duration-300" data-id="element-3158">
          <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6" data-id="element-3159">
            <CheckCircle className="h-10 w-10" data-id="element-3160" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3161">
            Laporan Berhasil!
          </h2>
          <p className="text-gray-600 mb-8 max-w-xs" data-id="element-3162">
            Laporan Anda telah tersimpan dengan ID{' '}
            <span className="font-mono font-bold text-gray-900" data-id="element-3163">
              #RPT-2024-0892
            </span>
          </p>
          <div className="flex flex-col gap-3 w-full max-w-xs" data-id="element-3164">
            <Link to="/officer" data-id="element-3165">
              <Button className="w-full bg-primary-600 hover:bg-primary-700" data-id="element-3166">
                Kembali ke Dashboard
              </Button>
            </Link>
            <Button variant="outline" onClick={() => {
            setIsSuccess(false);
            setStep(1);
            setFormData(initialFormData);
            setUploadedPhotos([]);
          }} data-id="element-3167">
              Buat Laporan Baru
            </Button>
          </div>
        </div>
      </OfficerLayout>;
  }
  return <OfficerLayout title="Laporan Kegiatan" data-id="element-3168">
      {/* Sync Indicator */}
      <div className="flex items-center justify-between mb-4 px-1" data-id="element-3169">
        <div className="flex items-center gap-2 text-xs" data-id="element-3170">
          {isOnline ? <span className="flex items-center gap-1 text-green-600" data-id="element-3171">
              <Wifi className="h-3 w-3" data-id="element-3172" /> Online
            </span> : <span className="flex items-center gap-1 text-orange-600" data-id="element-3173">
              <WifiOff className="h-3 w-3" data-id="element-3174" /> Offline (queued)
            </span>}
        </div>
        {lastSaved && <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-3175">
            <Save className="h-3 w-3" data-id="element-3176" /> Tersimpan{' '}
            {lastSaved.toLocaleTimeString('id-ID', {
          hour: '2-digit',
          minute: '2-digit'
        })}
          </span>}
      </div>

      {/* Tabs */}
      <div className="flex p-1 bg-gray-100 rounded-lg mb-6" data-id="element-3177">
        <button className={cn('flex-1 py-2 text-sm font-medium rounded-md transition-all', activeTab === 'create' ? 'bg-white shadow text-primary-600' : 'text-gray-500')} onClick={() => setActiveTab('create')} data-id="element-3178">
          Buat Baru
        </button>
        <button className={cn('flex-1 py-2 text-sm font-medium rounded-md transition-all', activeTab === 'history' ? 'bg-white shadow text-primary-600' : 'text-gray-500')} onClick={() => setActiveTab('history')} data-id="element-3179">
          Riwayat
        </button>
      </div>

      {activeTab === 'create' ? <>
          <ProgressStepper steps={['Kategori', 'Detail', 'Lokasi', 'Peserta', 'Bukti', 'Review']} currentStep={step} onStepClick={s => s < step && setStep(s)} data-id="element-3180" />

          <div className="mt-4 mb-28" data-id="element-3181">
            {/* Step 1: Kategori Laporan */}
            {step === 1 && <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3182">
                <div className="space-y-2" data-id="element-3183">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3184">
                    Kategori Laporan <span className="text-red-500" data-id="element-3185">*</span>
                  </label>
                  <div className="grid grid-cols-1 gap-3" data-id="element-3186">
                    {[{
                value: 'program',
                label: 'Program',
                desc: 'Pilah Sampah, Cegah Sampah, Sirkular Ekonomi'
              }, {
                value: 'series',
                label: 'Series Tematik',
                desc: 'HPSN, Ramadhan Minim Sampah, Idul Fitri'
              }, {
                value: 'event',
                label: 'Event',
                desc: 'HPSN, Mega Clean-Up, Business Matching'
              }].map(cat => <button key={cat.value} onClick={() => setFormData({
                ...formData,
                category: cat.value as any,
                programId: ''
              })} className={cn('p-4 rounded-lg border-2 text-left transition-all', formData.category === cat.value ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300')} data-id="element-3187">
                        <p className="font-medium text-gray-900" data-id="element-3188">{cat.label}</p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-3189">{cat.desc}</p>
                      </button>)}
                  </div>
                  {errors.category && <p className="text-xs text-red-500" data-id="element-3190">{errors.category}</p>}
                </div>

                {formData.category && <div className="space-y-2 animate-in fade-in duration-200" data-id="element-3191">
                    <label className="text-sm font-medium text-gray-700" data-id="element-3192">
                      Pilih{' '}
                      {formData.category === 'program' ? 'Program' : formData.category === 'series' ? 'Series' : 'Event'}{' '}
                      <span className="text-red-500" data-id="element-3193">*</span>
                    </label>
                    <Select value={formData.programId} onChange={e => setFormData({
              ...formData,
              programId: e.target.value
            })} className={errors.programId ? 'border-red-500' : ''} data-id="element-3194">
                      <option value="" data-id="element-3195">-- Pilih --</option>
                      {formData.category === 'program' && <>
                          <option value="pilah" data-id="element-3196">Pilah Sampah dari Rumah</option>
                          <option value="cegah" data-id="element-3197">Cegah Sampah Plastik</option>
                          <option value="sirkular" data-id="element-3198">Ekonomi Sirkular</option>
                        </>}
                      {formData.category === 'series' && <>
                          <option value="hpsn" data-id="element-3199">HPSN 2025</option>
                          <option value="ramadhan" data-id="element-3200">
                            Ramadhan Minim Sampah
                          </option>
                          <option value="idul_fitri" data-id="element-3201">
                            Idul Fitri Minim Sampah
                          </option>
                        </>}
                      {formData.category === 'event' && <>
                          <option value="hpsn_event" data-id="element-3202">
                            HPSN 2025: Atasi Sampah Plastik
                          </option>
                          <option value="cleanup" data-id="element-3203">
                            Mega Clean-Up Ciliwung
                          </option>
                          <option value="business" data-id="element-3204">
                            Business Matching Daur Ulang
                          </option>
                        </>}
                    </Select>
                    {errors.programId && <p className="text-xs text-red-500" data-id="element-3205">{errors.programId}</p>}
                  </div>}
              </div>}

            {/* Step 2: Detail Kegiatan */}
            {step === 2 && <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3206">
                <div className="space-y-2" data-id="element-3207">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3208">
                    Judul Kegiatan <span className="text-red-500" data-id="element-3209">*</span>
                  </label>
                  <Input placeholder="Contoh: Sosialisasi Pilah Sampah RT 05" value={formData.title} onChange={e => setFormData({
              ...formData,
              title: e.target.value
            })} className={errors.title ? 'border-red-500' : ''} data-id="element-3210" />
                  {errors.title && <p className="text-xs text-red-500" data-id="element-3211">{errors.title}</p>}
                </div>

                <div className="space-y-2" data-id="element-3212">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3213">
                    Jenis Kegiatan <span className="text-red-500" data-id="element-3214">*</span>
                  </label>
                  <Select value={formData.activityType} onChange={e => setFormData({
              ...formData,
              activityType: e.target.value
            })} className={errors.activityType ? 'border-red-500' : ''} data-id="element-3215">
                    <option value="" data-id="element-3216">-- Pilih Jenis --</option>
                    <option value="kampanye" data-id="element-3217">Kampanye Tatap Muka</option>
                    <option value="edukasi" data-id="element-3218">Edukasi / Penyuluhan</option>
                    <option value="sosialisasi" data-id="element-3219">Sosialisasi</option>
                    <option value="event" data-id="element-3220">Event / Aksi Bersih</option>
                  </Select>
                  {errors.activityType && <p className="text-xs text-red-500" data-id="element-3221">
                      {errors.activityType}
                    </p>}
                </div>

                <div className="space-y-2" data-id="element-3222">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3223">
                    Deskripsi Singkat <span className="text-red-500" data-id="element-3224">*</span>
                    <span className="text-gray-400 font-normal" data-id="element-3225">
                      {' '}
                      (min. 50 karakter)
                    </span>
                  </label>
                  <Textarea placeholder="Jelaskan jalannya kegiatan, materi yang disampaikan, dan respon peserta..." className={`h-32 ${errors.description ? 'border-red-500' : ''}`} value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} data-id="element-3226" />
                  <div className="flex justify-between" data-id="element-3227">
                    {errors.description && <p className="text-xs text-red-500" data-id="element-3228">
                        {errors.description}
                      </p>}
                    <p className={`text-xs ml-auto ${formData.description.length < 50 ? 'text-orange-500' : 'text-green-600'}`} data-id="element-3229">
                      {formData.description.length}/50 karakter
                    </p>
                  </div>
                </div>
              </div>}

            {/* Step 3: Lokasi & Waktu */}
            {step === 3 && <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3230">
                <div className="grid grid-cols-2 gap-4" data-id="element-3231">
                  <div className="space-y-2" data-id="element-3232">
                    <label className="text-sm font-medium text-gray-700" data-id="element-3233">
                      Tanggal <span className="text-red-500" data-id="element-3234">*</span>
                    </label>
                    <Input type="date" value={formData.date} onChange={e => setFormData({
                ...formData,
                date: e.target.value
              })} className={errors.date ? 'border-red-500' : ''} data-id="element-3235" />
                    {errors.date && <p className="text-xs text-red-500" data-id="element-3236">{errors.date}</p>}
                  </div>
                  <div className="space-y-2" data-id="element-3237">
                    <label className="text-sm font-medium text-gray-700" data-id="element-3238">
                      Waktu
                    </label>
                    <Input type="time" value={formData.time} onChange={e => setFormData({
                ...formData,
                time: e.target.value
              })} data-id="element-3239" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-3240">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3241">
                    Lokasi GPS <span className="text-red-500" data-id="element-3242">*</span>
                  </label>
                  <div className="flex gap-2" data-id="element-3243">
                    <Input placeholder="Latitude, Longitude" readOnly className="bg-gray-50 flex-1" value={formData.latitude && formData.longitude ? `${formData.latitude}, ${formData.longitude}` : ''} data-id="element-3244" />
                    <Button variant="outline" size="icon" className="shrink-0" onClick={handleGetLocation} disabled={isGettingLocation} data-id="element-3245">
                      {isGettingLocation ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-3246" /> : <Navigation className="h-4 w-4" data-id="element-3247" />}
                    </Button>
                  </div>
                  {formData.latitude && formData.longitude ? <p className="text-xs text-green-600 flex items-center" data-id="element-3248">
                      <CheckCircle className="h-3 w-3 mr-1" data-id="element-3249" /> GPS Akurat (±5m)
                    </p> : <p className="text-xs text-orange-500 flex items-center" data-id="element-3250">
                      <AlertTriangle className="h-3 w-3 mr-1" data-id="element-3251" /> Klik tombol
                      untuk ambil lokasi
                    </p>}
                  {errors.location && <p className="text-xs text-red-500" data-id="element-3252">{errors.location}</p>}
                </div>

                <div className="space-y-2" data-id="element-3253">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3254">
                    Nama Lokasi (Opsional)
                  </label>
                  <Input placeholder="Contoh: Balai RW 05, Kelurahan Sukasari" value={formData.locationName} onChange={e => setFormData({
              ...formData,
              locationName: e.target.value
            })} data-id="element-3255" />
                </div>

                {/* Map Preview Placeholder */}
                <div className="h-32 bg-gray-100 rounded-lg border flex items-center justify-center" data-id="element-3256">
                  <div className="text-center" data-id="element-3257">
                    <MapPin className="h-6 w-6 text-gray-400 mx-auto mb-1" data-id="element-3258" />
                    <p className="text-xs text-gray-500" data-id="element-3259">Preview Peta</p>
                  </div>
                </div>
              </div>}

            {/* Step 4: Partisipasi */}
            {step === 4 && <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3260">
                <div className="space-y-2" data-id="element-3261">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3262">
                    Jumlah Peserta <span className="text-red-500" data-id="element-3263">*</span>
                  </label>
                  <Input type="number" placeholder="0" min="1" value={formData.participants} onChange={e => setFormData({
              ...formData,
              participants: e.target.value
            })} className={errors.participants ? 'border-red-500' : ''} data-id="element-3264" />
                  {errors.participants && <p className="text-xs text-red-500" data-id="element-3265">
                      {errors.participants}
                    </p>}
                </div>

                <div className="space-y-2" data-id="element-3266">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3267">
                    Sasaran Peserta <span className="text-red-500" data-id="element-3268">*</span>
                    <span className="text-gray-400 font-normal" data-id="element-3269">
                      {' '}
                      (pilih yang sesuai)
                    </span>
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-id="element-3270">
                    {TARGET_AUDIENCE_OPTIONS.map(option => <button key={option.id} onClick={() => toggleTargetAudience(option.id)} className={cn('p-3 rounded-lg border text-left text-sm transition-all flex items-center gap-2', formData.targetAudience.includes(option.id) ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300 text-gray-700')} data-id="element-3271">
                        <div className={cn('h-4 w-4 rounded border flex items-center justify-center', formData.targetAudience.includes(option.id) ? 'bg-primary-600 border-primary-600' : 'border-gray-300')} data-id="element-3272">
                          {formData.targetAudience.includes(option.id) && <CheckCircle className="h-3 w-3 text-white" data-id="element-3273" />}
                        </div>
                        {option.label}
                      </button>)}
                  </div>
                  {errors.targetAudience && <p className="text-xs text-red-500" data-id="element-3274">
                      {errors.targetAudience}
                    </p>}
                </div>
              </div>}

            {/* Step 5: Evidence Upload */}
            {step === 5 && <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3275">
                <div className="space-y-2" data-id="element-3276">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3277">
                    Foto Kegiatan <span className="text-red-500" data-id="element-3278">*</span>
                    <span className="text-gray-400 font-normal" data-id="element-3279">
                      {' '}
                      (min. 1, max. 5)
                    </span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer active:scale-95" data-id="element-3280">
                    <div className="h-12 w-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-3" data-id="element-3281">
                      <Camera className="h-6 w-6 text-primary-600" data-id="element-3282" />
                    </div>
                    <p className="text-sm font-medium text-gray-900" data-id="element-3283">
                      Ambil Foto Kegiatan
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-3284">
                      Foto akan menyimpan lokasi & waktu otomatis
                    </p>
                  </div>
                  {errors.photos && <p className="text-xs text-red-500" data-id="element-3285">{errors.photos}</p>}
                </div>

                {uploadedPhotos.length > 0 && <div className="space-y-2" data-id="element-3286">
                    <div className="flex justify-between items-center" data-id="element-3287">
                      <p className="text-sm font-medium text-gray-700" data-id="element-3288">
                        Foto Terupload ({uploadedPhotos.length}/5)
                      </p>
                      <span className="text-xs text-green-600 flex items-center" data-id="element-3289">
                        <CheckCircle className="h-3 w-3 mr-1" data-id="element-3290" /> Cukup
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2" data-id="element-3291">
                      {uploadedPhotos.map((photo, i) => <div key={i} className="aspect-square bg-gray-200 rounded-lg relative overflow-hidden group" data-id="element-3292">
                          <img src={photo} alt={`Evidence ${i + 1}`} className="w-full h-full object-cover" data-id="element-3293" />
                          <button onClick={() => setUploadedPhotos(uploadedPhotos.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5" data-id="element-3294">
                            <X className="h-3 w-3" data-id="element-3295" />
                          </button>
                        </div>)}
                    </div>
                  </div>}

                <div className="space-y-2" data-id="element-3296">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3297">
                    Video (Opsional)
                  </label>
                  <div className="flex gap-2" data-id="element-3298">
                    <Input placeholder="URL YouTube atau link video" value={formData.videoUrl} onChange={e => setFormData({
                ...formData,
                videoUrl: e.target.value
              })} data-id="element-3299" />
                    <Button variant="outline" size="icon" data-id="element-3300">
                      <Video className="h-4 w-4" data-id="element-3301" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2" data-id="element-3302">
                  <label className="text-sm font-medium text-gray-700" data-id="element-3303">
                    URL Bukti Tayang (Opsional)
                  </label>
                  <div className="flex gap-2" data-id="element-3304">
                    <Input placeholder="Link berita/sosmed jika ada publikasi" value={formData.proofUrl} onChange={e => setFormData({
                ...formData,
                proofUrl: e.target.value
              })} data-id="element-3305" />
                    <Button variant="outline" size="icon" data-id="element-3306">
                      <LinkIcon className="h-4 w-4" data-id="element-3307" />
                    </Button>
                  </div>
                </div>
              </div>}

            {/* Step 6: Review */}
            {step === 6 && <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3308">
                <Card data-id="element-3309">
                  <CardContent className="p-4 space-y-4" data-id="element-3310">
                    <div className="flex justify-between items-start" data-id="element-3311">
                      <h3 className="font-bold text-gray-900" data-id="element-3312">
                        Ringkasan Laporan
                      </h3>
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setStep(1)} data-id="element-3313">
                        <Edit2 className="h-3 w-3 mr-1" data-id="element-3314" /> Edit
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-3315">
                      <div data-id="element-3316">
                        <p className="text-xs text-gray-500" data-id="element-3317">Kategori</p>
                        <p className="font-medium capitalize" data-id="element-3318">
                          {formData.category || '-'}
                        </p>
                      </div>
                      <div data-id="element-3319">
                        <p className="text-xs text-gray-500" data-id="element-3320">
                          Program/Series/Event
                        </p>
                        <p className="font-medium" data-id="element-3321">
                          {formData.programId || '-'}
                        </p>
                      </div>
                      <div className="col-span-2" data-id="element-3322">
                        <p className="text-xs text-gray-500" data-id="element-3323">Judul Kegiatan</p>
                        <p className="font-medium" data-id="element-3324">{formData.title || '-'}</p>
                      </div>
                      <div data-id="element-3325">
                        <p className="text-xs text-gray-500" data-id="element-3326">Tanggal</p>
                        <p className="font-medium" data-id="element-3327">{formData.date || '-'}</p>
                      </div>
                      <div data-id="element-3328">
                        <p className="text-xs text-gray-500" data-id="element-3329">Peserta</p>
                        <p className="font-medium" data-id="element-3330">
                          {formData.participants || '0'} Orang
                        </p>
                      </div>
                      <div className="col-span-2" data-id="element-3331">
                        <p className="text-xs text-gray-500" data-id="element-3332">Sasaran</p>
                        <div className="flex flex-wrap gap-1 mt-1" data-id="element-3333">
                          {formData.targetAudience.map(id => {
                      const option = TARGET_AUDIENCE_OPTIONS.find(o => o.id === id);
                      return <Badge key={id} variant="secondary" className="text-[10px]" data-id="element-3334">
                                {option?.label}
                              </Badge>;
                    })}
                        </div>
                      </div>
                      <div className="col-span-2" data-id="element-3335">
                        <p className="text-xs text-gray-500" data-id="element-3336">Lokasi</p>
                        <p className="font-medium text-xs font-mono" data-id="element-3337">
                          {formData.latitude}, {formData.longitude}
                        </p>
                      </div>
                    </div>

                    <div data-id="element-3338">
                      <p className="text-xs text-gray-500" data-id="element-3339">Deskripsi</p>
                      <p className="text-sm text-gray-700 mt-1" data-id="element-3340">
                        {formData.description || '-'}
                      </p>
                    </div>

                    <div data-id="element-3341">
                      <p className="text-xs text-gray-500 mb-2" data-id="element-3342">
                        Bukti Foto ({uploadedPhotos.length})
                      </p>
                      <div className="flex gap-2" data-id="element-3343">
                        {uploadedPhotos.map((photo, i) => <div key={i} className="h-12 w-12 bg-gray-200 rounded bg-cover" style={{
                    backgroundImage: `url(${photo})`
                  }} data-id="element-3344" />)}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 flex gap-3" data-id="element-3345">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" data-id="element-3346" />
                  <p className="text-xs text-yellow-800" data-id="element-3347">
                    Pastikan data sudah benar. Laporan yang sudah dikirim tidak
                    dapat diubah.
                  </p>
                </div>

                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border" data-id="element-3348">
                  <input type="checkbox" id="declaration" className="h-4 w-4 rounded border-gray-300 text-primary-600" data-id="element-3349" />
                  <label htmlFor="declaration" className="text-sm text-gray-700" data-id="element-3350">
                    Saya menyatakan data yang diisi adalah benar dan dapat
                    dipertanggungjawabkan.
                  </label>
                </div>
              </div>}
          </div>

          {/* Navigation Buttons */}
          <div className="fixed bottom-20 left-0 right-0 z-40 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]" data-id="element-3351">
            <div className="container mx-auto max-w-xl px-4 py-4 bg-white border-t flex gap-3" data-id="element-3352">
              {step > 1 && <Button variant="outline" onClick={handleBack} className="flex-1" disabled={isSubmitting} data-id="element-3353">
                  Kembali
                </Button>}
              {step < 6 ? <Button onClick={handleNext} className="flex-1 bg-primary-600 hover:bg-primary-700" data-id="element-3354">
                  Lanjut
                </Button> : <Button onClick={handleSubmit} className="flex-1 bg-primary-600 hover:bg-primary-700" disabled={isSubmitting} data-id="element-3355">
                  {isSubmitting ? <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="element-3356" />
                      Mengirim...
                    </> : 'Kirim Laporan'}
                </Button>}
            </div>
          </div>
        </> : <div className="space-y-3 animate-in fade-in duration-300" data-id="element-3357">
          {/* Filter Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar" data-id="element-3358">
            {['Semua', 'Draft', 'Menunggu', 'Disetujui', 'Revisi'].map(filter => <button key={filter} className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap bg-white border border-gray-200 text-gray-600 hover:bg-gray-50" data-id="element-3359">
                  {filter}
                </button>)}
          </div>

          {mockHistory.map(report => <Card key={report.id} className="active:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelectedReport(report)} data-id="element-3360">
              <CardContent className="p-4" data-id="element-3361">
                <div className="flex justify-between items-start mb-2" data-id="element-3362">
                  <Badge variant={report.status === 'approved' ? 'success' : report.status === 'pending' ? 'warning' : 'destructive'} className="text-[10px] px-2" data-id="element-3363">
                    {report.status === 'approved' ? 'Disetujui' : report.status === 'pending' ? 'Menunggu' : 'Ditolak'}
                  </Badge>
                  <span className="text-xs text-gray-500" data-id="element-3364">{report.date}</span>
                </div>
                <h4 className="font-bold text-gray-900" data-id="element-3365">{report.title}</h4>
                <p className="text-xs text-gray-500 mt-1" data-id="element-3366">{report.program}</p>
                <div className="flex items-center justify-between mt-3" data-id="element-3367">
                  <span className="text-xs font-mono text-gray-400" data-id="element-3368">
                    #{report.id}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-300" data-id="element-3369" />
                </div>
              </CardContent>
            </Card>)}
        </div>}

      {/* Report Detail Modal */}
      <Modal isOpen={!!selectedReport} onClose={() => setSelectedReport(null)} title={`Detail Laporan #${selectedReport?.id}`} size="sm" className="mb-0 rounded-b-none sm:rounded-b-lg sm:mb-auto fixed bottom-0 sm:relative w-full sm:w-auto" data-id="element-3370">
        {selectedReport && <div className="space-y-4" data-id="element-3371">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border" data-id="element-3372">
              <span className="text-sm text-gray-600" data-id="element-3373">Status Approval</span>
              <Badge variant={selectedReport.status === 'approved' ? 'success' : selectedReport.status === 'pending' ? 'warning' : 'destructive'} data-id="element-3374">
                {selectedReport.status === 'approved' ? 'Disetujui' : selectedReport.status === 'pending' ? 'Menunggu Review' : 'Perlu Revisi'}
              </Badge>
            </div>

            {selectedReport.status === 'rejected' && <div className="p-3 bg-red-50 border border-red-100 rounded-lg" data-id="element-3375">
                <p className="text-xs font-bold text-red-800 mb-1" data-id="element-3376">
                  Catatan Reviewer:
                </p>
                <p className="text-sm text-red-700" data-id="element-3377">{selectedReport.note}</p>
                <Button size="sm" variant="outline" className="w-full mt-2 border-red-200 text-red-700 bg-white" data-id="element-3378">
                  Edit & Kirim Ulang
                </Button>
              </div>}

            <div data-id="element-3379">
              <h4 className="font-medium text-gray-900 mb-2" data-id="element-3380">
                {selectedReport.title}
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4" data-id="element-3381">
                <div data-id="element-3382">
                  <p className="text-xs text-gray-500" data-id="element-3383">Program</p>
                  <p data-id="element-3384">{selectedReport.program}</p>
                </div>
                <div data-id="element-3385">
                  <p className="text-xs text-gray-500" data-id="element-3386">Tanggal</p>
                  <p data-id="element-3387">{selectedReport.date}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg" data-id="element-3388">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                eiusmod tempor incididunt ut labore et dolore magna aliqua.
              </p>
            </div>

            <div data-id="element-3389">
              <h4 className="font-medium text-gray-900 mb-2" data-id="element-3390">Bukti Foto</h4>
              <div className="grid grid-cols-3 gap-2" data-id="element-3391">
                {[1, 2].map(i => <div key={i} className="aspect-square bg-gray-200 rounded-lg" data-id="element-3392" />)}
              </div>
            </div>

            <Button className="w-full" variant="outline" onClick={() => setSelectedReport(null)} data-id="element-3393">
              Tutup
            </Button>
          </div>}
      </Modal>
    </OfficerLayout>;
}